<script setup lang="ts">
import { AppLink } from '#components'

const { slice, index } = defineProps({
  slice: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  slices: {
    type: Array,
    required: true,
  },
})

const lazy = computed(() => {
  return index > 0
})

function getElement(to: string | null | undefined) {
  return to ? AppLink : 'div'
}

const widescreenImage = computed(() => {
  return useXcdnImage(slice?.File?.Media?.url, 'f=rs:fit:2560')
})

const desktopImage = computed(() => {
  return useXcdnImage(slice?.File?.Media?.url, 'f=rs:fit:1440')
})

const tabletImage = computed(() => {
  return useXcdnImage(slice?.File?.Media?.url, 'f=rs:fit:1280')
})

const phabletImage = computed(() => {
  return useXcdnImage(slice?.File?.MediaMobile?.url || slice?.File?.Media?.url, 'f=rs:fit:1024')
})

const mobileImage = computed(() => {
  return useXcdnImage(slice?.File?.MediaMobile?.url || slice?.File?.Media?.url, 'f=rs:fit:640')
})
</script>

<template>
  <section
    :id="slice?.Settings?.AnchorId || `slice-${index}`"
    class="content-type content-type--full-width ct-hero"
  >
    <Head
      v-if="!lazy && slice?.File?.Media?.url && slice?.File?.Media?.mime?.includes('image')"
      render-priority="10"
    >
      <Link
        key="hero-widescreen"
        rel="preload"
        as="image"
        :href="widescreenImage"
        fetchpriority="high"
        media="(min-width: 1280px)"
      />
      <Link
        key="hero-desktop"
        rel="preload"
        as="image"
        :href="desktopImage"
        fetchpriority="high"
        media="(min-width: 1024px)"
      />
      <Link
        key="hero-tablet"
        rel="preload"
        as="image"
        :href="tabletImage"
        fetchpriority="high"
        media="(min-width: 768px)"
      />
      <Link
        key="hero-phablet"
        rel="preload"
        as="image"
        :href="phabletImage"
        fetchpriority="high"
        media="(min-width: 640px)"
      />
      <Link
        key="hero-mobile"
        rel="preload"
        as="image"
        :href="mobileImage"
        fetchpriority="high"
        media="(max-width: 639px)"
      />
    </Head>

    <h1 class="ct-hero__heading">
      <component
        :is="getElement(slice.File?.Link)"
        :to="slice.File?.Link || '#'"
        class="ct-hero__link"
      >
        <span>
          {{ slice.Title }}
          {{ slice.Title2 }}
        </span>
      </component>
    </h1>

    <div class="ct-hero__container">
      <div class="ct-hero__content">
        <div
          class="ct-hero__heading"
          aria-hidden="true"
        >
          <span
            class="ct-hero__heading--1"
          >{{ slice.Title }}</span>
          <span
            class="ct-hero__heading--2"
          >{{ slice.Title2 }}</span>
        </div>

        <p
          v-if="slice.Description"
          class="ct-hero__description"
        >
          {{ slice.Description }}
        </p>

        <div
          v-if="slice?.Cards?.length"
          class="ct-hero__actions"
        >
          <template
            v-for="(card, cardIndex) in slice.Cards"
            :key="card"
          >
            <div
              v-if="card?.Image?.url"
              class="ct-hero__cta"
              :style="`--index: ${cardIndex};`"
            >
              <component
                :is="getElement(card.Link)"
                :to="card.Link || '#'"
                class="ct-hero__title"
              >
                {{ card.Title }}
              </component>
              <img
                :src="useXcdnImage(card.Image.url, 'f=rs:fit:310')"
                :alt="card.Image.alternativeText || card.Title || ''"
                :height="card.Image.height"
                :width="card.Image.width"
                loading="lazy"
                fetchpriority="auto"
                decoding="async"
              >
              <div class="ct-hero__button">
                <Icon name="icon:arrow" />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="ct-hero__visual">
      <picture v-if="slice?.File?.Media?.url && slice?.File?.Media?.mime?.includes('image')">
        <source
          :srcset="widescreenImage"
          media="(min-width: 1280px)"
        >
        <source
          :srcset="desktopImage"
          media="(min-width: 1024px)"
        >
        <source
          :srcset="tabletImage"
          media="(min-width: 768px)"
        >
        <source
          :srcset="phabletImage"
          media="(min-width: 640px)"
        >
        <source
          :srcset="mobileImage"
        >
        <img
          class="ct-hero__image"
          :src="widescreenImage"
          :width="slice?.File?.Media?.width"
          :height="slice?.File?.Media?.height"
          :loading="lazy ? 'lazy' : 'eager'"
          :fetchpriority="lazy ? 'low' : 'high'"
          :decoding="lazy ? 'async' : 'sync'"
        >
      </picture>
      <div
        v-else-if="slice?.File?.Media?.url && slice?.File?.Media?.mime?.includes('video')"
        class="ct-hero__video"
      >
        <UiVideo
          v-if="slice?.File?.Media?.url"
          :class="{ 'mq--desktop-plus': slice?.File?.Media?.url && slice?.File?.MediaMobile?.url }"
          :src="slice?.File?.Media?.url"
          :alt="slice?.File?.Media?.alternativeText || slice?.Name"
          :width="slice?.File?.Media?.width"
          :height="slice?.File?.Media?.height"
          :loading="lazy ? 'lazy' : 'eager'"
          :fetchpriority="lazy ? 'auto' : 'high'"
          :controls="false"
          autoplay
          muted
          loop
        />
        <UiVideo
          v-if="slice?.File?.MediaMobile?.url && slice?.File?.MediaMobile?.mime?.includes('video')"
          class="mq--mobile-phablet"
          :src="slice?.File?.MediaMobile?.url"
          :alt="slice?.File?.MediaMobile?.alternativeText || slice?.Name"
          :width="slice?.File?.MediaMobile?.width"
          :height="slice?.File?.MediaMobile?.height"
          :loading="lazy ? 'lazy' : 'eager'"
          :fetchpriority="lazy ? 'auto' : 'high'"
          :controls="false"
          autoplay
          muted
          loop
        />
      </div>
    </div>
  </section>

  <!-- <pre>slice: {{ slice }}</pre> -->
</template>

<style>
@import url("~layers/app/components/Slices/Hero.css");
</style>
